/**
 * FRODO Report Component
 * 
 * Displays FRODO optimization report in the simulation results drawer.
 * Shows statistics on rides offloaded to fixed routes.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Button,
  Divider,
  Paper,
} from '@mui/material';

import type { FrodoReport, FrodoOffloadedRide } from '../../types/frodo';
import CombinedTripsPerHourChart from '../map/components/simulation-results/CombinedTripsPerHourChart';
import { binRequestsPerHour } from '../map/components/simulation-results/utils';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../utils/iconFallbacks';
interface FrodoReportProps {
  report: FrodoReport;
  offloadedRides?: FrodoOffloadedRide[];
  regularRidePoints?: any[];
}

export default function FrodoReportComponent({ report, offloadedRides, regularRidePoints }: FrodoReportProps) {
  const { t } = useTranslation('simulation');
  const handleExportCSV = () => {
    // Generate CSV content
    const headers = [
      'Route Name',
      'Route ID',
      'Ride Count',
      'Avg Wait Time (min)',
      'Avg Origin Distance (m)',
      'Avg Dest Distance (m)',
      'Departure Fixed',
      'Arrival Fixed',
      'Transfer Rides',
      'Avg Transfer Wait (min)',
    ];

    const rows = report.routeStats.map((stat) => [
      stat.routeName,
      stat.routeId,
      stat.rideCount.toString(),
      stat.avgWaitTime.toFixed(2),
      stat.avgOriginDistance.toFixed(2),
      stat.avgDestDistance.toFixed(2),
      stat.rideTypeBreakdown.departure_fixed.toString(),
      stat.rideTypeBreakdown.arrival_fixed.toString(),
      stat.transferStats?.transferRideCount?.toString() || '0',
      stat.transferStats?.avgTransferWaitTime?.toFixed(2) || '0',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.join(',')),
    ].join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `frodo-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleExportJSON = () => {
    const jsonContent = JSON.stringify(report, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `frodo-report-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Card sx={{ mb: 3, bgcolor: 'background.paper' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('results.frodo.title')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Icon size={20} name="file-arrow-down" />}
              onClick={handleExportCSV}
            >
              {t('results.frodo.exportCsv')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Icon size={20} name="file-arrow-down" />}
              onClick={handleExportJSON}
            >
              {t('results.frodo.exportJson')}
            </Button>
          </Box>
        </Box>

        {/* Summary and Chart Section */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mb: 3, alignItems: 'stretch' }}>
          {/* Summary Section - Left Column */}
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              {t('results.frodo.summary')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Paper
                elevation={2}
                sx={{
                  p: 1.2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                  borderRadius: 2,
                  background: 'rgba(255,255,255,0.96)',
                  minHeight: 80,
                  boxShadow: '0 2px 8px 0 rgba(0,0,0,0.06)',
                  transition: 'box-shadow 0.2s',
                  '&:hover': { boxShadow: '0 4px 16px 0 rgba(0,0,0,0.10)' },
                }}
              >
                <Box fontSize={22} color="primary.main">
                  <Icon size={20} name="route" />
                </Box>
                <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ fontSize: 18, lineHeight: 1 }}>
                  {report.totalOffloaded}
                </Typography>
                <Typography variant="caption" color="textSecondary" fontWeight={500} textAlign="center" sx={{ fontSize: 12, lineHeight: 1.1 }}>
                  {t('results.frodo.totalOffloaded')}
                </Typography>
              </Paper>
              
              <Paper
                elevation={2}
                sx={{
                  p: 1.2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                  borderRadius: 2,
                  background: 'rgba(255,255,255,0.96)',
                  minHeight: 80,
                  boxShadow: '0 2px 8px 0 rgba(0,0,0,0.06)',
                  transition: 'box-shadow 0.2s',
                  '&:hover': { boxShadow: '0 4px 16px 0 rgba(0,0,0,0.10)' },
                }}
              >
                <Box fontSize={22} color="text.primary">
                  <Icon size={20} name="car-side" />
                </Box>
                <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ fontSize: 18, lineHeight: 1 }}>
                  {report.totalRemaining}
                </Typography>
                <Typography variant="caption" color="textSecondary" fontWeight={500} textAlign="center" sx={{ fontSize: 12, lineHeight: 1.1 }}>
                  {t('results.frodo.remainingOnDemand')}
                </Typography>
              </Paper>
              
              <Paper
                elevation={2}
                sx={{
                  p: 1.2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                  borderRadius: 2,
                  background: 'rgba(255,255,255,0.96)',
                  minHeight: 80,
                  boxShadow: '0 2px 8px 0 rgba(0,0,0,0.06)',
                  transition: 'box-shadow 0.2s',
                  '&:hover': { boxShadow: '0 4px 16px 0 rgba(0,0,0,0.10)' },
                }}
              >
                <Box fontSize={22} color="success.main">
                  <Icon size={20} d={ICON_FALLBACKS.TrendingUp} />
                </Box>
                <Typography variant="h6" fontWeight={700} color="success.main" sx={{ fontSize: 18, lineHeight: 1 }}>
                  {report.offloadPercentage.toFixed(1)}%
                </Typography>
                <Typography variant="caption" color="textSecondary" fontWeight={500} textAlign="center" sx={{ fontSize: 12, lineHeight: 1.1 }}>
                  {t('results.frodo.offloadPercentage')}
                </Typography>
              </Paper>
            </Box>
          </Box>

          {/* Trips Per Hour Chart - Right Column */}
          {(offloadedRides && offloadedRides.length > 0) || (regularRidePoints && regularRidePoints.length > 0) ? (
            <Box sx={{ flex: 1, minWidth: 420 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                {t('results.frodo.tripsPerHour')}
              </Typography>
              <CombinedTripsPerHourChart
                regularTripsData={regularRidePoints ? binRequestsPerHour(regularRidePoints) : []}
                frodoTripsData={offloadedRides ? binRequestsPerHour(offloadedRides) : []}
              />
            </Box>
          ) : null}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Route Statistics */}
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          {t('results.frodo.routeStatistics')}
        </Typography>
        {report.routeStats.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('results.frodo.noRoutes')}
          </Typography>
        ) : (
          report.routeStats.map((routeStat) => (
            <Accordion key={routeStat.routeId} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<Icon size={20} name="chevron-down" />}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {routeStat.routeName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('results.frodo.idLabel', { id: routeStat.routeId })}
                    </Typography>
                  </Box>
                  <Chip
                    label={t('results.frodo.ridesChip', { count: routeStat.rideCount })}
                    color="primary"
                    size="small"
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Basic Statistics */}
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      {t('results.frodo.basicStatistics')}
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t('results.frodo.avgWaitTime')}
                        </Typography>
                        <Typography variant="body1">
                          {t('results.frodo.avgWaitTimeValue', { value: routeStat.avgWaitTime.toFixed(1) })}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t('results.frodo.avgOriginDistance')}
                        </Typography>
                        <Typography variant="body1">
                          {t('results.frodo.avgOriginDistanceValue', { value: routeStat.avgOriginDistance.toFixed(0) })}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t('results.frodo.avgDestDistance')}
                        </Typography>
                        <Typography variant="body1">
                          {t('results.frodo.avgDestDistanceValue', { value: routeStat.avgDestDistance.toFixed(0) })}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Ride Type Breakdown */}
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      {t('results.frodo.rideTypeBreakdown')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Chip
                        label={t('results.frodo.departureFixed', { count: routeStat.rideTypeBreakdown.departure_fixed })}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={t('results.frodo.arrivalFixed', { count: routeStat.rideTypeBreakdown.arrival_fixed })}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </Box>

                  {/* Transfer Statistics */}
                  {routeStat.transferStats && routeStat.transferStats.transferRideCount > 0 && (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        {t('results.frodo.transferStatistics')}
                      </Typography>
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2">
                          {t('results.frodo.transferRides', { count: routeStat.transferStats.transferRideCount })}
                        </Typography>
                        <Typography variant="body2">
                          {t('results.frodo.avgTransferWait', { value: routeStat.transferStats.avgTransferWaitTime.toFixed(1) })}
                        </Typography>
                      </Box>
                      {routeStat.transferStats.transferRoutes && routeStat.transferStats.transferRoutes.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                            {t('results.frodo.transferRouteCombinations')}
                          </Typography>
                          {routeStat.transferStats.transferRoutes.map((transferRoute, idx) => (
                            <Chip
                              key={idx}
                              label={`${routeStat.routeName} → ${transferRoute.routeName} (${transferRoute.count})`}
                              size="small"
                              variant="outlined"
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Timing Statistics */}
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      {t('results.frodo.timingStatistics')}
                    </Typography>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2">
                        {t('results.frodo.earliestRequest', { date: new Date(routeStat.timingStats.earliestRequest).toLocaleString() })}
                      </Typography>
                      <Typography variant="body2">
                        {t('results.frodo.latestRequest', { date: new Date(routeStat.timingStats.latestRequest).toLocaleString() })}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                        {t('results.frodo.waitTimeDistribution')}
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('results.frodo.timeRange')}</TableCell>
                            <TableCell align="right">{t('results.frodo.count')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {routeStat.timingStats.waitTimeDistribution.map((dist, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{dist.range}</TableCell>
                              <TableCell align="right">{dist.count}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </CardContent>
    </Card>
  );
}

